import { DoLogin, ReadMe } from './authentication';
import { ReadProfiles } from './profile';

export type PairingDestination = 'select-profile' | 'catalog';

export type PairingResult =
  | {
      ok: true;
      jwt: string;
      user: unknown;
      /**
       * Raw DoLogin payload (jwt + user + any token fields). The caller persists
       * this verbatim via CoreContext.setAuthentication so the context effect can
       * derive authentication.jwt -> setToken() and authenticated service calls work.
       */
      authentication: unknown;
      profiles: unknown[];
      activeProfile: unknown | null;
      destination: PairingDestination;
    }
  | { ok: false; reason: 'invalid' | 'expired-token' | 'network' };

/**
 * Pure orchestration of the TV device-pairing login flow.
 *
 * Extracted from Home/index.tsx (which is istanbul-ignored, so the orchestration
 * had zero coverage). Lives in services/** so it lands in the coverage include glob.
 *
 * No React, no navigation, no side effects beyond the service calls — the caller
 * (Home) maps the typed result to context setters + navigation.
 */
export const pairWithCode = async (code: string): Promise<PairingResult> => {
  const login = await DoLogin({ code });

  // ServerFetch returns `false` (or a non-object falsy) when the underlying fetch
  // throws — i.e. a connectivity/network failure. This must NOT be conflated with
  // an invalid code: an invalid code yields a truthy error object (without a jwt).
  if (!login || typeof login !== 'object') {
    return { ok: false, reason: 'network' };
  }

  if (!login.jwt) {
    return { ok: false, reason: 'invalid' };
  }

  const me = await ReadMe(login.jwt);
  if (me?.error && me?.status === 401) {
    return { ok: false, reason: 'expired-token' };
  }

  const rawProfiles = await ReadProfiles(login.jwt);
  const profiles = Array.isArray(rawProfiles) ? rawProfiles : [];
  const destination: PairingDestination = profiles.length ? 'select-profile' : 'catalog';

  return {
    ok: true,
    jwt: login.jwt,
    user: login.user,
    authentication: login,
    profiles,
    activeProfile: me?.active_profile ?? null,
    destination,
  };
};
